import { useQuery } from "@apollo/client";
import "components/Comments/CommentList";
import {
  SNAPSHOT_MISSING_ERROR_CODE,
  SNAPSHOT_PENDING_ERROR_CODE,
  useSnapshotPendingPolling,
} from "components/snapshotState";
import { gql } from "generated-graphql";
import "hooks/usePayment";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { getGraphQLErrorCode } from "../../../../utils/errorUtils";

export const FACILITY_REPORT_DETAILS_FRAGMENT = gql(`
  fragment FacilityReportDetailsFragment on Facility {
      id
      name
      county
      companyName
      naicsCode
      customerFacilityId
      tenantId
      tenant {
        id
        name
      }
      stateFields {
        key
        value
        type
        jurisdiction
        inputType
        label
        tooltip
        selectOptions {
          label
          value
        }
      }
      status
      dateClosed
      dateInactive
      isNotReporting
      isNotReportingThroughEncamp
      colocationReportingFacilityId
      colocationReportingFacility {
        id
        name
        customerFacilityId
      }
      reportsManually
      city
      state
      streetAddress1
      streetAddress2
      zip
      mailingStreetAddress1
      mailingStreetAddress2
      mailingCity
      mailingState
      mailingZip
      mailingCountry
      portalCredentials {
        ...PortalCredentialFragment
      }
      jobs {
        id
        action
        status
        createdAt
        updatedAt
        logs {
          id
          jobId
          createdAt
          status
          message
          cloudWatchLogStream
          screenshotKey
          screenshotUrl
          updatedAt
        }
        stepsDone {
          id
          jobId
          name
          createdAt
          updatedAt
        }
        automationDiagnoses {
          id
          category
          confidence
          summary
          suggestedFix
          affectedSelector
          failedStep
          missingData
          notes
          model
          mode
          turnsUsed
          toolsUsed
          estimatedCost
          fixLoopId
          iterationNumber
          createdAt
        }
        automationFixLoops {
          id
          success
          stopReason
          iterationCount
          maxIterations
          totalCost
          prUrl
          prBranch
          source
          iterationDetails
          createdAt
          diagnoses {
            id
            category
            confidence
            summary
            suggestedFix
            affectedSelector
            failedStep
            missingData
            notes
            iterationNumber
            createdAt
          }
        }
      }
      contacts {
        id
        reportingRoles
        person {
          ...PersonFragment
        }
      }
      facilityAlternateIds {
        id
        type
        value
        hasNoId
      }
      lepc {
        id
        name
        email
        mailingAddress
        mailingAddressLine2
        city
        state
        county
        zip
        isEmailed
        isMailed
        phone
        additionalComments
        needsT2sFile
        isStateSubmits
        cupa {
          cupaCode
          dueDate
          dueDateIsAnniversary
        }
      }
      supplementalLepcs {
        id
        lepc {
          id
          name
          email
          mailingAddress
          mailingAddressLine2
          city
          state
          county
          zip
          isEmailed
          isMailed
          phone
          additionalComments
          needsT2sFile
          isStateSubmits
          cupa {
            dueDate
            dueDateIsAnniversary
          }
        }
      }
      fireDepartment {
        id
        fireDeptId
        name
        email
        mailingAddress
        mailingAddressLine2
        city
        state
        county
        zip
        isMailed
        isEmailed
        phone
        phoneExt
        additionalComments
        stateSubmitsToFireDept
        needsT2sFile
      }
      supplementalFireDepartments {
        id
        fireDepartment {
          id
          fireDeptId
          name
          email
          mailingAddress
          mailingAddressLine2
          city
          state
          county
          zip
          isMailed
          isEmailed
          phone
          phoneExt
          additionalComments
          stateSubmitsToFireDept
          needsT2sFile
        }
      }
  }
`);

export const PERSON_FRAGMENT = gql(`
  fragment PersonFragment on Person {
    id
    first
    last
    email
    tenantId
    title
    city
    state
    streetAddress1
    zip
    phones {
      number
      type
    }
  }
`);

export const PORTAL_CREDENTIAL_FRAGMENT = gql(
  `
  fragment PortalCredentialFragment on PortalCredential {
    credential {
      id
      isEncampOwned
      kind
      password
      securityQuestions {
        question
        answer
      }
      username
      lastVerificationCheckAt
      lastVerificationSuccessAt
      verificationStatus
      tenantId
      usesCombinedInvoicing
      usesRcoCredential
      usesEManifestApi
      portal {
        id
        type
        jurisdiction
        verifiable
        url
        portalTech
      }
      credentialPortalFacilities {
        stateIdType
        stateIdValue
      }
      activeLock {
        holderId
        holderEmail
        expiresAt
      }
    }
  }
`
);

export const GET_TIERII_REPORT = gql(`
query GetTierIIReport($reportId: ID!, $version: String) {
  tierIIReport(reportId: $reportId, version: $version) {
    id
    tenantId
    reportingYear
    reportKind
    productCount
    organizationStatus
    encampStatus
    tags
    filedOn
    portalReportId
    reasonForReport
    fulfillmentNote
    needsRevision
    needsRevisionReason
    issues {
      key
    }
    tenant {
      id
      name
      partnerTenantId
      partnerTenant {
        id
        name
      }
      contacts {
        id
        type
        firstName
        lastName
        email
        user {
          person {
            first
            last
          }
        }
      }
    }
    reviewers {
      isComplete
    }
   facility {
    ...FacilityReportDetailsFragment
   }
    cohortMemberships {
      id
      cohort {
        id
        type
        status
        allowsMultipleCohorts
        reportingYear
        reportKind
        state
        tenantId
        partnerTenantId
        fdId
        lepcId
        credentialUsername
        alternateIdValue
        members {
          id
          status
        }
        currentWorkflow {
          id
          status
          currentActivity {
            id
            type
          }
        }
      }
    }
    currentWorkflow {
      id
      activities {
        id
        type
        status
        executionPlan
        description
        message
        championId
        nonChampionIds
        cohortFacilityIds
        reportDetailsExtended
        output
        inputs {
          id
          activityId
          label
          shortName
          value
          required
          type
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            updatedAt
            createdAt
            sizeMegabytes
          }
        }
        currentJob {
          id
          status
          action
          parameters
        }
        payments {
          id
          status
          paidAt
          tenantId
          amount
          lobMail {
            id
            lobId
            status
            type
          }
        }
      }
      tags {
        id
        name
      }
      currentActivityId
      currentActivity {
        id
        type
        status
        executionPlan
        description
        message
        assigneeId
        currentJobId
        nonChampionIds
        currentJob {
          id
          taskArn
          taskUrl
        }
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        documents  {
          document {
            id
            title
            description
            storageLink
            documentType
            updatedAt
            createdAt
          }
        }
      }
    }
    documents {
      id
      title
      description
      storageLink
      documentType
      updatedAt
      createdAt
      activities {
        id
        type
        description
      }
    }
    verifierFullName
    verifierTitle
    verifierPhone
    verifierEmail
    verifiedAt
    comments {
      ...CommentList
    }
    payments {
      ...PaymentRow
    }
    facilityOnlyPayments {
      id
      payee
      paidAt
      amount
      payeeType
      status
      paymentMethod
    }
    facilityPaymentHistory {
      id
      payee
      paidAt
      amount
      payeeType
      status
      paymentMethod
    }
  }
}`);

export function useReportDetails() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");

  const query = useQuery(GET_TIERII_REPORT, {
    variables: {
      reportId: reportId ?? "",
      // Transitional argument to do side-by-side-testing.
      version: version ?? undefined,
    },
    skip: !reportId,
  });

  // Auto-poll (with a hard timeout) while the snapshot is being created so the
  // report appears as soon as the async job finishes.
  const { pollingTimedOut } = useSnapshotPendingPolling(query);

  const errorCode = getGraphQLErrorCode(query.error);

  // Spread (not mutate) the QueryResult, memoized so identity stays stable
  // except when the query or the flag actually changes.
  const result = useMemo(
    () => ({ ...query, snapshotPollingTimedOut: pollingTimedOut }),
    [query, pollingTimedOut]
  );

  // SNAPSHOT_PENDING / SNAPSHOT_MISSING_ERROR_CODE are surfaced to the UI via
  // `query.error`; everything else preserves the existing throw-to-Oops path.
  if (
    query.error &&
    errorCode !== SNAPSHOT_PENDING_ERROR_CODE &&
    errorCode !== SNAPSHOT_MISSING_ERROR_CODE
  ) {
    throw query.error;
  }

  return result;
}
